import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { Skeleton } from "../components/ui/Skeleton";
import {
  clientBillingApi,
  ClientBillingPlan,
  ClientBillingRow,
  ConnectStatus,
} from "../api/clientBillingApi";

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase() === "USD" ? "USD" : currency,
  }).format(cents / 100);
}

const ClientBillingPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [connect, setConnect] = useState<ConnectStatus | null>(null);
  const [plans, setPlans] = useState<ClientBillingPlan[]>([]);
  const [rows, setRows] = useState<ClientBillingRow[]>([]);
  const [linkBusy, setLinkBusy] = useState(false);
  const [planName, setPlanName] = useState("");
  const [stripePriceId, setStripePriceId] = useState("");
  const [planAmountCents, setPlanAmountCents] = useState("");
  const [planSaving, setPlanSaving] = useState(false);
  const [checkoutBusyId, setCheckoutBusyId] = useState<string | null>(null);
  const [selectedPlanByClient, setSelectedPlanByClient] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    setError("");
    try {
      const [c, p, r] = await Promise.all([
        clientBillingApi.getConnectStatus(),
        clientBillingApi.listPlans(),
        clientBillingApi.listClients(),
      ]);
      setConnect(c);
      setPlans(p);
      setRows(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load client billing.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const startOnboarding = async () => {
    setLinkBusy(true);
    setError("");
    try {
      const { url } = await clientBillingApi.createConnectAccountLink();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start Stripe Connect onboarding.");
    } finally {
      setLinkBusy(false);
    }
  };

  const addPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    const cents = Number.parseInt(planAmountCents, 10);
    if (!planName.trim() || !stripePriceId.trim() || Number.isNaN(cents) || cents < 0) {
      setError("Enter plan name, Stripe Price ID, and amount in cents (0 to pull from Stripe).");
      return;
    }
    setPlanSaving(true);
    setError("");
    try {
      await clientBillingApi.createPlan({
        name: planName.trim(),
        stripePriceId: stripePriceId.trim(),
        amountCents: cents,
      });
      setPlanName("");
      setStripePriceId("");
      setPlanAmountCents("");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save plan.");
    } finally {
      setPlanSaving(false);
    }
  };

  const subscribeClient = async (clientId: string) => {
    const planId = selectedPlanByClient[clientId];
    if (!planId) {
      setError("Select a billing plan for this client.");
      return;
    }
    setCheckoutBusyId(clientId);
    setError("");
    try {
      const { url } = await clientBillingApi.createCheckout(clientId, planId);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start checkout.");
    } finally {
      setCheckoutBusyId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Client billing</h1>
          <p className="mt-1 text-sm text-slate-400">
            Connect Stripe to charge clients on your own connected account.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-900/20 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full rounded-xl bg-slate-800" />
            <Skeleton className="h-40 w-full rounded-xl bg-slate-800" />
          </div>
        ) : (
          <>
            <section className="rounded-xl border border-slate-800 bg-slate-900/80 p-6">
              <h2 className="text-lg font-semibold text-white">Stripe Connect</h2>
              <p className="mt-1 text-sm text-slate-400">
                Complete onboarding so Checkout sessions run on your connected account.
              </p>
              {connect && (
                <ul className="mt-4 space-y-2 text-sm text-slate-300">
                  <li>Account: {connect.hasAccount ? connect.stripeAccountId ?? "—" : "Not created yet"}</li>
                  <li>Charges: {connect.chargesEnabled ? "enabled" : "not enabled"}</li>
                  <li>Payouts: {connect.payoutsEnabled ? "enabled" : "not enabled"}</li>
                  <li>Details submitted: {connect.detailsSubmitted ? "yes" : "no"}</li>
                  <li>
                    Ready for client checkout:{" "}
                    <span className={connect.onboardingComplete ? "text-emerald-400" : "text-amber-300"}>
                      {connect.onboardingComplete ? "Yes" : "Complete onboarding"}
                    </span>
                  </li>
                </ul>
              )}
              <button
                type="button"
                onClick={() => void startOnboarding()}
                disabled={linkBusy}
                className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {linkBusy ? "Opening Stripe…" : connect?.onboardingComplete ? "Update in Stripe" : "Start / continue onboarding"}
              </button>
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/80 p-6">
              <h2 className="text-lg font-semibold text-white">Client plans</h2>
              <p className="mt-1 text-sm text-slate-400">
                Map a Stripe Price (from your Connect product catalog) to a label your team recognizes.
              </p>
              <form onSubmit={(e) => void addPlan(e)} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <input
                  className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                  placeholder="Plan name"
                  value={planName}
                  onChange={(e) => setPlanName(e.target.value)}
                />
                <input
                  className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                  placeholder="price_…"
                  value={stripePriceId}
                  onChange={(e) => setStripePriceId(e.target.value)}
                />
                <input
                  className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                  placeholder="Amount cents (0 = from Stripe)"
                  inputMode="numeric"
                  value={planAmountCents}
                  onChange={(e) => setPlanAmountCents(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={planSaving}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {planSaving ? "Saving…" : "Add plan"}
                </button>
              </form>
              {plans.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">No plans yet.</p>
              ) : (
                <ul className="mt-4 divide-y divide-slate-800 border-t border-slate-800">
                  {plans.map((p) => (
                    <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                      <span className="font-medium text-white">{p.name}</span>
                      <span className="text-slate-400">
                        {formatMoney(p.amountCents, p.currency)} / {p.billingInterval}
                      </span>
                      <code className="text-xs text-slate-500">{p.stripePriceId}</code>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-xl border border-slate-800 bg-slate-900/80 p-6">
              <h2 className="text-lg font-semibold text-white">Clients</h2>
              <p className="mt-1 text-sm text-slate-400">
                Send a subscription checkout link. CROA first-service date is set when a dispute letter is marked sent.
              </p>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-500">
                      <th className="pb-2 pr-4">Client</th>
                      <th className="pb-2 pr-4">Subscription</th>
                      <th className="pb-2 pr-4">CROA service start</th>
                      <th className="pb-2">Checkout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.clientId} className="border-b border-slate-800/60 text-slate-200">
                        <td className="py-3 pr-4">
                          <div className="font-medium text-white">
                            {r.firstName} {r.lastName}
                          </div>
                          <div className="text-xs text-slate-500">{r.email}</div>
                        </td>
                        <td className="py-3 pr-4">
                          {r.subscriptionStatus || "—"}
                          {r.planName ? <div className="text-xs text-slate-500">{r.planName}</div> : null}
                        </td>
                        <td className="py-3 pr-4 text-slate-400">
                          {r.croaFirstServiceDate
                            ? new Date(r.croaFirstServiceDate).toLocaleDateString()
                            : "—"}
                        </td>
                        <td className="py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-white"
                              value={selectedPlanByClient[r.clientId] ?? ""}
                              onChange={(e) =>
                                setSelectedPlanByClient((prev) => ({
                                  ...prev,
                                  [r.clientId]: e.target.value,
                                }))
                              }
                            >
                              <option value="">Plan…</option>
                              {plans.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              disabled={
                                checkoutBusyId === r.clientId || plans.length === 0 || !connect?.chargesEnabled
                              }
                              onClick={() => void subscribeClient(r.clientId)}
                              className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                              title={!connect?.chargesEnabled ? "Complete Stripe Connect onboarding first" : undefined}
                            >
                              {checkoutBusyId === r.clientId ? "Redirecting…" : "Subscribe link"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ClientBillingPage;
